<script lang="ts" setup>
import {capitalize, ref} from 'vue';
import {usePartnerConfig} from "~/stores/usePartnerConfig";
import {useLocaleStore} from "~/stores/useLocaleStore";
import MainLogo from "~/components/MainLogo.vue";
import IconWechat from "~/components/sx/icons/footer/IconWechat.vue";
import {onClickOutside} from "@vueuse/core";
import IconTg from "~/components/sx/icons/footer/IconTg.vue";
import IconMail from "~/components/sx/icons/footer/IconMail.vue";
import {UiFlagIcon, UiLanguages} from '@common/ui-kit';
import IconSxWhatsApp from "~/components/sx/icons/IconSxWhatsApp.vue";

const { t, locale } = useI18n();
const { config, getUserCountry, telegramLink, supportEmail, qrValue, whatsappLink, getCountryCode } = usePartnerConfig();
const { setLocale, mappedLanguages, currentLanguage } = useLocaleStore();

const wechat = ref<HTMLDivElement | null>(null);
const whatsapp = ref<HTMLDivElement | null>(null);

const visibleCode = ref({
  wechat: false,
  whatsapp: false,
});

const isLangModalVisible = ref(false);

const items = computed(() => {
  return [
    { name: 'components.footer.lawEnforcement', link: '/law-enforcement', terms: false },
    { name: 'components.footer.bugBounty', link: 'https://t.me/BugBountySxBot', terms: false },
    { name: 'components.footer.termsOfUse', link: config.value?.terms_and_condition, terms: true },
    { name: 'components.footer.privacyPolicy', link: config.value?.privacy_and_policy, terms: true },
    { name: 'components.footer.returnPolicy', link: config.value?.returns_policy, terms: true },
    { name: 'components.footer.faq', link: '/faq' },
  ];
});

const texts = computed(() => {
  return {
    title: t('components.footer.companyInfo', { country: getUserCountry(locale.value) }),
    subtitle: t('components.footer.cookiesInfo'),
  }
});

function removeStream(str: string) {
  return str.replace(/\/stream$/, '');
}

const toggleCode = (target: 'wechat' | 'whatsapp') => {
  visibleCode.value.wechat = false;
  visibleCode.value.whatsapp = false;
  visibleCode.value[target] = !visibleCode.value[target];
};

onMounted(async () => {
  await getCountryCode();

  onClickOutside(wechat.value, () => {
    if (wechat.value) {
      visibleCode.value.wechat = false;
    }
  });
  onClickOutside(whatsapp.value, () => {
    if (whatsapp.value) {
      visibleCode.value.whatsapp = false;
    }
  });
});
</script>

<template>
  <footer class="app-footer">
    <div class="app-footer__inner">
      <div class="app-footer__section">
        <div class="app-footer__logo">
          <main-logo sx link :black="false" />
        </div>
        <div class="app-footer__item-list">
          <a
            v-for="(item, index) in items"
            :key="index"
            :href="removeStream(item.link!)"
            :target="'_blank'"
            :rel="'noopener noreferrer'"
            class="app-footer__item"
          >
            {{ $t(item.name) }}
          </a>
          <UiLanguages
            v-model:is-show="isLangModalVisible"
            v-model="currentLanguage"
            :locales="mappedLanguages"
            for-header
            @change="setLocale($event)"
            class="app-footer__language"
          >
            <template #trigger>
              <div class="langs-trigger default-tag">
                <UiFlagIcon :isoCode="currentLanguage?.isoCode || 'en'" />
                {{ capitalize(currentLanguage?.isoCode || 'en') }}
              </div>
            </template>
          </UiLanguages>
        </div>
      </div>

      <div class="app-footer__section column">
        <div class="app-footer__text">
          {{ texts.title }}
        </div>
        <div class="app-footer__info">
          <div class="app-footer__text gray mw-880">
            {{ texts.subtitle }}
          </div>
          <div class="app-footer__icons">
            <span style="position: relative" @click="toggleCode('wechat')">
              <icon-wechat class="app-footer__icon" />
              <span ref="wechat" :class="['qr-code', { visible: visibleCode.wechat }]">
                 <AppQrCode
                   v-if="qrValue"
                   :link="qrValue"
                   :options="{ width: 132, height: 132, errorCorrectionLevel: 'H', margin: 2 }"
                 />
              </span>
            </span>
            <span style="position: relative" @click="toggleCode('whatsapp')">
              <icon-sx-whats-app color="#fff" class="app-footer__icon" />
              <span ref="whatsapp" :class="['qr-code', { visible: visibleCode.whatsapp }]">
                <AppQrCode
                  :link="whatsappLink"
                  :options="{ width: 132, height: 132, errorCorrectionLevel: 'H', margin: 2 }"
                />
              </span>
            </span>
            <a
              v-if="telegramLink"
              :href="telegramLink"
              target="_blank"
              :rel="'noopener noreferrer'"
              aria-label="Join our Telegram bot"
            >
              <icon-tg color="#fff" class="app-footer__icon" />
            </a>
            <a :href="`mailto:${supportEmail}`" aria-label="Mail us">
              <icon-mail color="#fff" class="app-footer__icon" />
            </a>
          </div>
        </div>
      </div>
    </div>

    <div class="app-footer__video">
      <client-only>
        <lazy-sx-video-lines loop footer />
      </client-only>
    </div>
  </footer>
</template>

<style lang="scss" scoped>
.qr-code {
  position: absolute;
  bottom: 32px;
  left: 50%;
  transform: translateX(-50%) scale(0.8);
  transition: 0.3s;
  opacity: 0;
  pointer-events: none;
  &.visible {
    opacity: 1;
    transform: translateX(-30%) scale(1);
    pointer-events: auto;
  }
}

.blur {
  background: linear-gradient(
      180deg,
      rgb(99 99 99 / 5%) 90%,
      rgb(157 29 29 / 3%) 0%
  );
  position: absolute;
  bottom: 0;
  z-index: 1;
  height: 100%;
  width: 100%;
}

.app-footer {
  background: var(--bg-sx-black);
  padding: 4rem 2rem;
  display: flex;
  justify-content: center;
  position: relative;
  overflow: hidden;
  font-family: AlsHauss, sans-serif;
  @media (min-width: 680px)  {
    padding: 80px 2rem 35px;
    align-items: flex-start;
    height: 512px;
  }

  &__icons {
    display: flex;
    align-items: center;
    gap: 16px
  }
  &__icon {
    background: #222325;
    border-radius: 4px;
    height: 24px;
    width: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    transition: .3s;
    cursor: pointer;
    &:hover {
      background: var(--bg-sx-black-hover);
    }
  }
  &__inner {
    z-index: 2;
    max-width: 1200px;

    @media (min-width: 480px) {
      align-items: center;
    }
  }
  &__video {
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    position: absolute;
    display: none;
    z-index: 0;
    @media (min-width: 480px)  {
      display: block;
    }
    :deep(video),
    :deep(canvas) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
  &__language {
    display: flex;
    align-items: center;
    border: none;
    background: transparent;
    cursor: pointer;
    transition: .3s;
    .langs-trigger {
      display: flex;
      align-items: center;
      gap: 4px;
      color: white;
      font-weight: 500;
      font-size: 14px;
      line-height: 16px;
    }
    :deep(.ui-languages--trigger) {
      background: transparent;
      box-shadow: 0 0 0 1px #323232;
      height: 28px;
      width: 60px;
      max-width: 60px;
      padding: 0 8px;
      &:hover {
        box-shadow: 0 0 0 1px #fff;
      }
    }
  }
  &__flag {
    :deep(svg) {
      width: 22px;
      height: 16px;
    }
  }
  &__locale {
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--font-sx-color-white);
  }
  &__section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 28px;
    padding-top: 24px;
    gap: 16px;
    &:first-child {
      padding-bottom: 24px;
      padding-top: 0;
      border-bottom: var(--border-sx-contrast);
    }
    &.column {
      flex-direction: column;
      align-items: flex-start;
    }
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__info {
    width: 100%;
    gap: 24px;
    display: flex;
    justify-content: space-between;
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__logo {
    :deep(.logo) {
      max-width: 100px;
      min-width: 100px;
      max-height: 20px;
      img {
        object-fit: cover;
      }
    }
  }
  &__item-list {
    display: flex;
    align-items: center;
    gap: 24px;
    @media (max-width: 880px) {
      flex-direction: column;
    }
  }
  &__item {
    color: var(--font-sx-color-white);
    font-weight: 500;
    font-size: 14px;
    line-height: 16px;
    letter-spacing: 0;
    transition: .3s;
    &:hover {
      text-decoration: underline;
    }
  }
  &__text {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    letter-spacing: 0;
    color: var(--font-sx-color-white);
    &.gray {
      color: var(--font-sx-color-gray);
    }
    &.mw-880 {
      max-width: 880px;
    }
  }
}
</style>
